import { useMemo } from 'react';

import { DEFAULT_ORDER_TYPES } from '@/components/History/OrderHistory/Filters/Desktop';
import {
  mergeCachedTransactionsIntoTrades,
  RECENT_TRANSACTION_WINDOW_MS,
} from '@/components/History/OrderHistory/utils';
import { parseHistoryChainId } from '@/components/History/utils';

import { useLimitOrders } from '@/hooks/useLimitOrders';
import {
  useWalletTradeHistory,
  type LimitOrderFunders,
  type TradeHistoryFilters,
} from '@/hooks/useWalletOpenLimitOrders';

import { transactionsSelector, useTransactionStore } from '@/store/transaction';

import { getTradeIdentity, isSuccessfulHistoryEntry } from '@/utils/0x/history/status';
import type { TokenResult } from '@/utils/0x/token-registry.types';
import {
  isEthereumAddress,
  type ChainAddress,
  type EthereumAddress,
  type SolanaAddress,
} from '@/utils/models';
import { normalizeTokenAddress, tokenKey } from '@/utils/normalizeAddress';
import { applyOpenLimitOrderTombstones } from '@/utils/open-limit-order-tombstones';

export type HistoryScopeToken = Pick<TokenResult, 'address' | 'chainId' | 'symbol'>;

type TokenInvolvingTransaction = {
  sellToken: { address: string; chainId: number | string };
  buyToken: { address: string; chainId: number | string };
};

const involvesToken = (tx: TokenInvolvingTransaction, scopeToken: HistoryScopeToken) =>
  [tx.sellToken, tx.buyToken].some(
    (token) =>
      token.address.toLowerCase() === scopeToken.address.toLowerCase() &&
      parseHistoryChainId(token.chainId) === scopeToken.chainId,
  );

const scopeFilters = (tokens: HistoryScopeToken[] | undefined): TradeHistoryFilters => ({
  orderTypes: DEFAULT_ORDER_TYPES,
  advancedOrderStatus: 'filled',
  // Both status filters run before the API paginates, so a page of rows is a
  // page of settled fills and the lookahead row stays meaningful.
  successfulOnly: true,
  ...(tokens?.length && {
    tokenAddresses: [
      ...new Set(
        tokens.map((token) => normalizeTokenAddress(token.address, token.chainId) as ChainAddress),
      ),
    ],
    chainIds: [...new Set(tokens.map((token) => String(token.chainId)))],
  }),
});

/** One page of settled trades, plus a lookahead row that decides `hasNext`. */
export const useCompactOrderHistory = ({
  address,
  scopeTokens,
  scopeReady,
  limit,
  page,
  enabled: enabledOption = true,
}: {
  address: string | undefined;
  scopeTokens: HistoryScopeToken[] | undefined;
  scopeReady: boolean;
  limit: number;
  page: number;
  enabled?: boolean;
}) => {
  const funders: LimitOrderFunders =
    address && isEthereumAddress(address)
      ? { ethereum: address as EthereumAddress, solana: undefined }
      : { ethereum: undefined, solana: address as SolanaAddress | undefined };
  const enabled = enabledOption && !!address && scopeReady;

  // Fetch every row through the requested page so local trades merged in
  // front push API rows onto later pages instead of hiding them.
  const primary = useWalletTradeHistory({
    funders,
    filters: scopeFilters(scopeTokens),
    offset: 0,
    limit: page * limit + 1,
    enabled,
  });

  const trades = useMemo(() => {
    const seenKeys = new Set<string>();

    return (primary.data?.trades ?? []).filter((trade) => {
      const key = getTradeIdentity(trade);
      if (seenKeys.has(key)) return false;
      seenKeys.add(key);
      return true;
    });
  }, [primary.data?.trades]);

  const cachedTxsByChain = useTransactionStore(transactionsSelector)[address ?? ''];

  const candidates = useMemo(() => {
    const merged =
      !address || !cachedTxsByChain
        ? trades
        : mergeCachedTransactionsIntoTrades(trades, cachedTxsByChain, {
            recencyMs: RECENT_TRANSACTION_WINDOW_MS,
            accept: (tx) =>
              !scopeTokens?.length ||
              scopeTokens.every((scopeToken) => involvesToken(tx, scopeToken)),
          });

    return merged.filter(isSuccessfulHistoryEntry);
  }, [trades, cachedTxsByChain, address, scopeTokens]);

  return {
    rows: candidates.slice((page - 1) * limit, page * limit),
    hasNext: candidates.length > page * limit,
    isLoading: primary.isLoading,
    isError: primary.isError,
    refetch: primary.refetch,
  };
};

/** One page of open limit orders, minus ones cancelled locally that the indexer may still return. */
export const useCompactOpenOrders = ({
  address,
  scopeTokens,
  page,
  limit,
  enabled = true,
}: {
  address: string | undefined;
  scopeTokens: HistoryScopeToken[] | undefined;
  page: number;
  limit: number;
  enabled?: boolean;
}) => {
  const { data, isLoading, isError, refetch } = useLimitOrders({
    makers: address ? [address as ChainAddress] : undefined,
    tokens: scopeTokens?.length
      ? scopeTokens.map((token) => tokenKey(token.address, token.chainId)).join(',')
      : undefined,
    page,
    limit,
    options: { enabled },
  });

  const { records, total } = useMemo(
    () => (data ? applyOpenLimitOrderTombstones(data) : { records: [], total: 0 }),
    [data],
  );

  return { records, total, isLoading, isError, refetch };
};
